import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import tesoreriaService, { ChequeTerceroRechazoDocumento } from '../../services/tesoreriaService';

interface ChequeTerceroRechazoDocumentosSectionProps {
  nroCarga: number;
  fechaRechazo?: string | null;
  reloadToken?: number;
  showDivider?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  try {
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

const ChequeTerceroRechazoDocumentosSection: React.FC<ChequeTerceroRechazoDocumentosSectionProps> = ({
  nroCarga,
  fechaRechazo,
  reloadToken = 0,
  showDivider = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentos, setDocumentos] = useState<ChequeTerceroRechazoDocumento[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await tesoreriaService.getDocumentosRechazoChequeTercero(nroCarga);
        if (!cancelled) setDocumentos(data.documentos ?? []);
      } catch (e: unknown) {
        if (!cancelled) {
          const err = e as { response?: { data?: { message?: string } } };
          setError(err.response?.data?.message ?? 'No se pudieron cargar los documentos del rechazo.');
          setDocumentos([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [nroCarga, reloadToken]);

  return (
    <Box sx={{ mt: showDivider ? 2 : 0 }}>
      {showDivider && <Divider sx={{ mb: 2 }} />}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: showDivider ? 'error.main' : 'text.primary', mb: 1 }}>
        Documentos generados
      </Typography>
      {fechaRechazo && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Fecha de rechazo: {formatDate(fechaRechazo)}
        </Typography>
      )}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Cargando documentos…
          </Typography>
        </Stack>
      ) : error ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : documentos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay documentos registrados para este rechazo.
        </Typography>
      ) : (
        <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tercero</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CAE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentos.map((doc) => (
                <TableRow key={`${doc.tipo}-${doc.nroFormulario}`}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {doc.titulo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Form. {doc.nroFormulario}
                    </Typography>
                  </TableCell>
                  <TableCell>{doc.comprobante?.trim() || '-'}</TableCell>
                  <TableCell>{doc.nombreTercero?.trim() || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {doc.importe != null ? formatCurrency(doc.importe) : '-'}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(doc.fecha)}
                  </TableCell>
                  <TableCell>{doc.cae?.trim() || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ChequeTerceroRechazoDocumentosSection;
